import { defineComponent as _defineComponent } from 'vue'
import Vue, { ref, onMounted, computed, onBeforeUnmount } from "vue";
import { cloneDeep } from "lodash";
import deepEqual from "deep-equal";
import ThemeTextField from "@/components/molecules/ThemeTextField.vue";
import ButtonDefault from "@/components/atoms/ButtonDefault.vue";
import { showNotifyDialog } from "@/lib/dialog";
import type { Detail } from "@/lib/models/theme";
import { usePageEditStore } from "@/stores/pageEdit";
import { useThemeStore } from "@/stores/theme";
import { handleShortcutKeys, TriggerKey } from "@/lib/utils/keyboardShortcuts";
import { useI18n } from "vue-i18n-bridge";


export default /*#__PURE__*/_defineComponent({
  __name: 'ThemeEditor',
  props: {
    novelId: null
  },
  setup(__props: any) {

const props = __props as {
  novelId: string;
};



const { t } = useI18n();

const theme = ref<Record<string, string>>({});
const preTheme = ref<Record<string, string>>({});
const unbindShortcutKeys = ref<null | (() => void)>(null);

const pageEditStore = usePageEditStore();
const themeStore = useThemeStore();
const { fetchTheme, updateTheme } = themeStore;

onMounted(async () => {
  await fetchTheme(props.novelId);
  if (themeStore.theme.detail) {
    const detail: Record<string, string> = {};
    themeStore.theme.detail.forEach((item) => {
      detail[item.name] = item.value;
    });
    theme.value = detail;
    preTheme.value = cloneDeep(detail);
  }

  // 保存のショートカットキーの有効化と解除関数の取得
  unbindShortcutKeys.value = handleShortcutKeys([
    { trigger: TriggerKey.Meta, keys: ["s"], callback: submit },
    { trigger: TriggerKey.Ctrl, keys: ["s"], callback: submit },
  ]);
});

onBeforeUnmount(() => {
  // コンポーネントが破棄される前に保存のショートカットキーを解除
  if (unbindShortcutKeys.value) unbindShortcutKeys.value();
});

const isValidWordLength = computed(() => {
  // テーマの執筆予定文字数は未入力を許容する
  if (!theme.value["word-length"]) {
    return true;
  }

  // 半角数字のみで構成されているかチェックする正規表現
  const isNumeric = /^\d+$/.test(theme.value["word-length"]);
  // 半角数字のみである場合にtrueを返し、それ以外はfalseを返す
  return isNumeric;
});

const changed = computed(() => {
  const changed = !deepEqual(preTheme.value, theme.value);
  pageEditStore.setModified(changed);
  return changed;
});

async function submit() {
  if (!changed.value) return;

  if (!isValidWordLength.value) {
    await showNotifyDialog({
      title: t('themePage.inputErrorTitle'),
      content: t('themePage.inputErrorMessage'),
    });
    return;
  }

  const detail = Object.keys(theme.value).map((key) => ({
    name: key,
    value: theme.value[key],
  }));

  const input = {
    novelId: props.novelId,
    detail: filterWordLengthDetails(detail),
  };
  await updateTheme(input);

  Vue.notify({
    title: t('themePage.saveSuccessMessage'),
    type: "success",
  });

  pageEditStore.setModified(false);
  preTheme.value = cloneDeep(theme.value);
}

function filterWordLengthDetails(details: Detail[]) {
  const index = details.findIndex((detail) => detail.name === "word-length");
  if (index === -1) return details; // word-length属性が存在しない場合、元の配列を返す

  const wordLengthDetail = details[index];
  if (wordLengthDetail.value === "" || wordLengthDetail.value === undefined) {
    // word-length属性が空文字またはundefinedの場合、その要素を除外
    return details.filter((_, idx) => idx !== index);
  }

  return details;
}

function formatWordLength() {
  const parsedValue = parseInt(theme.value["word-length"], 10);
  if (!Number.isNaN(parsedValue)) {
    theme.value["word-length"] = parsedValue.toString();
  }
}

return { __sfc: true,props, t, theme, preTheme, unbindShortcutKeys, pageEditStore, themeStore, fetchTheme, updateTheme, isValidWordLength, changed, submit, filterWordLengthDetails, formatWordLength, ThemeTextField, ButtonDefault }
}

})